// Shared document number helpers (CNPJ)
export class DocumentNumberUtils {
    private static stripNonDigits(value?: string | null): string {
        return typeof value === 'string' ? value.replace(/\D/g, '') : '';
    }

    private static applyCnpjMask(numbers: string): string {
        return numbers
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})/, '$1-$2')
            .slice(0, 18);
    }

    private static applyCpfMask(numbers: string): string {
        return numbers
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .slice(0, 14);
    }

    private static calculateDigit(numbers: string, length: number): number {
        let sum = 0;
        let weight = length - 7;

        for (let i = 0; i < length; i += 1) {
            sum += Number.parseInt(numbers[i], 10) * weight;
            weight = weight === 2 ? 9 : weight - 1;
        }

        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    }

    private static calculateCpfDigit(numbers: string, length: number): number {
        let sum = 0;
        let weight = length + 1; // 10 for first digit (length 9), 11 for second (length 10)

        for (let i = 0; i < length; i += 1) {
            sum += Number.parseInt(numbers[i], 10) * weight;
            weight -= 1;
        }

        const remainder = (sum * 10) % 11;
        return remainder === 10 ? 0 : remainder;
    }

    /** Applies CNPJ mask to a string, tolerating partial input. */
    static maskCnpj(value?: string | null): string {
        const digits = this.stripNonDigits(value);
        if (!digits) return '';
        return this.applyCnpjMask(digits);
    }

    /** Removes mask characters and trims to 14 digits to avoid artificial suffixes. */
    static removeCnpjMask(value?: string | null): string {
        const digitsOnly = this.stripNonDigits(value);
        return digitsOnly.length > 14 ? digitsOnly.slice(0, 14) : digitsOnly;
    }

    /** Validates a CNPJ (rejects repeated digits and ensures check digits). */
    static isValidCnpj(value?: string | null): boolean {
        const numbers = this.removeCnpjMask(value);

        if (numbers.length !== 14 || /^(\d)\1+$/.test(numbers)) {
            return false;
        }

        const firstDigit = this.calculateDigit(numbers, 12);
        if (Number.parseInt(numbers[12], 10) !== firstDigit) {
            return false;
        }

        const secondDigit = this.calculateDigit(numbers, 13);
        return Number.parseInt(numbers[13], 10) === secondDigit;
    }

    /** Formats CNPJ with punctuation; returns empty string when no digits. */
    static formatCnpj(value?: string | null): string {
        const normalized = this.removeCnpjMask(value);
        return normalized ? this.applyCnpjMask(normalized) : '';
    }

    /** Applies CPF mask to a string, tolerating partial input. */
    static maskCpf(value?: string | null): string {
        const digits = this.stripNonDigits(value);
        if (!digits) return '';
        return this.applyCpfMask(digits);
    }

    /** Removes mask characters and trims to 11 digits. */
    static removeCpfMask(value?: string | null): string {
        const digitsOnly = this.stripNonDigits(value);
        return digitsOnly.length > 11 ? digitsOnly.slice(0, 11) : digitsOnly;
    }

    /** Validates a CPF (rejects repeated digits and ensures check digits). */
    static isValidCpf(value?: string | null): boolean {
        const numbers = this.removeCpfMask(value);

        if (numbers.length !== 11 || /^(\d)\1+$/.test(numbers)) {
            return false;
        }

        const firstDigit = this.calculateCpfDigit(numbers, 9);
        if (Number.parseInt(numbers[9], 10) !== firstDigit) {
            return false;
        }

        const secondDigit = this.calculateCpfDigit(numbers, 10);
        return Number.parseInt(numbers[10], 10) === secondDigit;
    }

    /** Formats CPF with punctuation; returns empty string when no digits. */
    static formatCpf(value?: string | null): string {
        const normalized = this.removeCpfMask(value);
        return normalized ? this.applyCpfMask(normalized) : '';
    }

    /**
     * Máscara dinâmica CPF/CNPJ: até 11 dígitos aplica máscara de CPF; acima,
     * de CNPJ. Útil em campos que aceitam pessoa física ou jurídica.
     */
    static maskCpfOrCnpj(value?: string | null): string {
        const digits = this.stripNonDigits(value).slice(0, 14);
        if (!digits) return '';
        return digits.length <= 11 ? this.applyCpfMask(digits) : this.applyCnpjMask(digits);
    }

    /** Remove a máscara e limita a 14 dígitos (CPF ou CNPJ). */
    static removeCpfOrCnpjMask(value?: string | null): string {
        return this.stripNonDigits(value).slice(0, 14);
    }
}
